/**
 * 渲染层文件链接路径补全工具
 *
 * 规则：渲染时把 `/relay/cosfile` 补全为 `/file-center/relay/cosfile`
 *   - 已经是 `/file-center/relay/cosfile` 的保持不变，不重复叠加
 *   - TUS 直传链路 `/tus/relay/cosfile` 保持不变（补全会变成 /tus/file-center/... 的错误地址）
 *
 * 仅用于展示层（图片、链接、预览、下载地址），不参与请求体与响应体的改写。
 *
 * 实现说明：使用严格字符串匹配（indexOf + slice + endsWith），不使用正则。
 * 这样既避免了后行断言（lookbehind）在 Safari 16.4 以下抛 SyntaxError 导致白屏，
 * 也让匹配规则完全可读、可预期。
 */

// 需要补全的原始路径
const SOURCE_PATH = '/relay/cosfile';
// 补全后的目标路径
const TARGET_PATH = '/file-center/relay/cosfile';

// 匹配位置前如果以这些前缀结尾，则保持原样
const SKIP_PREFIXES = ['/file-center', '/tus'];

// 匹配位置之后必须是这些字符之一（或字符串末尾），否则不算命中
// 用于排除 /relay/cosfileXxx 这类只是前缀相似的路径
const VALID_NEXT_CHARS = ['/', '?', '#'];

// 日志开关：默认开启，控制台执行 window.__RELAY_COSFILE_LOG__ = false 可关闭
function isLogEnabled() {
  if (typeof window === 'undefined') return false;
  return window.__RELAY_COSFILE_LOG__ !== false;
}

function truncate(str, max = 300) {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

/**
 * 对单个字符串做路径补全
 * @param {string} str 原始地址（完整 URL 或相对路径）
 * @param {string} [scene] 仅用于日志标识来源场景
 * @returns {string} 处理后的地址，未命中时原样返回
 */
export function replaceRelayCosfile(str, scene) {
  if (typeof str !== 'string' || str.indexOf(SOURCE_PATH) === -1) {
    return str;
  }

  /**
   * 从 from 位置开始，找下一个合法的匹配起点
   * 合法性 = 匹配片段之后是字符串末尾或 / ? # 这些分隔符
   */
  const findValidIndex = (from) => {
    let pos = str.indexOf(SOURCE_PATH, from);
    while (pos !== -1) {
      const end = pos + SOURCE_PATH.length;
      if (end === str.length || VALID_NEXT_CHARS.indexOf(str[end]) !== -1) {
        return pos;
      }
      pos = str.indexOf(SOURCE_PATH, pos + 1);
    }
    return -1;
  };

  let result = '';
  let cursor = 0;
  let changed = false;
  let index = findValidIndex(cursor);

  while (index !== -1) {
    // 先原样搬运本次匹配之前的内容
    result += str.slice(cursor, index);

    // 用原串匹配位置之前的内容判断前缀，严格比对结尾
    const beforeMatch = str.slice(0, index);
    const shouldSkip = SKIP_PREFIXES.some((prefix) => beforeMatch.endsWith(prefix));

    if (shouldSkip) {
      result += SOURCE_PATH;
    } else {
      result += TARGET_PATH;
      changed = true;
    }

    cursor = index + SOURCE_PATH.length;
    index = findValidIndex(cursor);
  }

  // 尾部剩余内容
  result += str.slice(cursor);

  if (!changed) {
    return str;
  }

  if (isLogEnabled()) {
    const label = scene ? `urlTransform/${scene}` : 'urlTransform';
    console.log(
      `[${label}] 链接已替换\n  原始: ${truncate(str)}\n  替换后: ${truncate(result)}`
    );
  }
  return result;
}

// ========================== 文件域名前缀映射 ==========================
//
// 历史数据里存在多套域名 + 路径组合的文件地址，统一收敛到 otob 文件服务：
//   dcops.woa.com / dcops.idc.tencent.com        -> otob.dcops.qq.com
//   dcops.test.woa.com / dcops.test.tencent.com  -> test.otob.dcops.qq.com
//
// 每条规则把目标路径直接写成最终形态（已含 /file-center），因此必须在
// replaceRelayCosfile 之前执行：若先做路径补全，/relay/cosfile 会提前变成
// /file-center/relay/cosfile，规则里的 from 就再也匹配不上了。
//
// 注意：from 不含协议，命中时会一并吞掉紧邻的 https:// / http:// / //，
// 避免出现 http://https://otob... 这种拼接错误。
const FILE_URL_PREFIX_RULES = [
  {
    from: 'dcops.woa.com/file-center/tus/relay/cosfile',
    to: 'https://otob.dcops.qq.com/file-center/tus/relay/cosfile',
  },
  {
    from: 'dcops.idc.tencent.com/file-center/tus/relay/cosfile',
    to: 'https://otob.dcops.qq.com/file-center/tus/relay/cosfile',
  },
  {
    from: 'dcops.woa.com/relay/cosfile',
    to: 'https://otob.dcops.qq.com/file-center/relay/cosfile',
  },
  {
    from: 'dcops.idc.tencent.com/relay/cosfile',
    to: 'https://otob.dcops.qq.com/file-center/relay/cosfile',
  },
  {
    from: 'dcops.test.woa.com/file-center/tus/relay/cosfile',
    to: 'https://test.otob.dcops.qq.com/file-center/tus/relay/cosfile',
  },
  {
    from: 'dcops.test.tencent.com/file-center/tus/relay/cosfile',
    to: 'https://test.otob.dcops.qq.com/file-center/tus/relay/cosfile',
  },
  {
    from: 'dcops.test.woa.com/relay/cosfile',
    to: 'https://test.otob.dcops.qq.com/file-center/relay/cosfile',
  },
  {
    from: 'dcops.test.tencent.com/relay/cosfile',
    to: 'https://test.otob.dcops.qq.com/file-center/relay/cosfile',
  },
];

// 长规则优先，避免短前缀在长前缀之前命中，导致替换结果被截断
const SORTED_PREFIX_RULES = FILE_URL_PREFIX_RULES.slice().sort(
  (a, b) => b.from.length - a.from.length
);

// 命中位置之后只允许这些字符（或字符串末尾），排除 /relay/cosfileXxx 这类近似路径
const PREFIX_VALID_NEXT_CHARS = ['/', '?', '#', '&'];
// 命中位置之前只允许这些字符（或字符串开头），排除 my-dcops.woa.com 这类伪造域名
const PREFIX_VALID_PREV_CHARS = ['/', '=', '&', '?'];
// 命中位置前紧邻的协议头，替换时一并吞掉（不用后行断言，兼容低版本 Safari）
const TRAILING_PROTOCOL = /(?:https?:)?\/\/$/;

function findPrefixMatch(str, from, startAt) {
  let pos = str.indexOf(from, startAt);
  while (pos !== -1) {
    const prevChar = pos > 0 ? str[pos - 1] : '';
    const end = pos + from.length;
    const nextChar = end < str.length ? str[end] : '';

    const prevOk = pos === 0 || PREFIX_VALID_PREV_CHARS.indexOf(prevChar) !== -1;
    const nextOk = end === str.length || PREFIX_VALID_NEXT_CHARS.indexOf(nextChar) !== -1;

    if (prevOk && nextOk) {
      const before = str.slice(0, pos);
      const protocolMatch = before.match(TRAILING_PROTOCOL);
      return {
        start: pos - (protocolMatch ? protocolMatch[0].length : 0),
        end,
      };
    }
    pos = str.indexOf(from, pos + 1);
  }
  return null;
}

/**
 * 按规则表把文件地址的域名 + 路径前缀统一替换为 otob 文件服务
 * @param {string} str 原始地址
 * @param {string} [scene] 仅用于日志标识来源场景
 * @returns {string} 处理后的地址，未命中时原样返回（同一引用）
 */
export function replaceFileUrlPrefix(str, scene) {
  // 所有规则都围绕 cosfile 路径，不含该关键字直接短路
  if (typeof str !== 'string' || str.indexOf('cosfile') === -1) {
    return str;
  }

  let result = '';
  let cursor = 0;
  let changed = false;

  while (cursor < str.length) {
    let bestMatch = null;
    let bestRule = null;

    // 在所有规则里取「最靠前」的一处命中，保证按原文顺序拼接
    for (let i = 0; i < SORTED_PREFIX_RULES.length; i += 1) {
      const rule = SORTED_PREFIX_RULES[i];
      const match = findPrefixMatch(str, rule.from, cursor);
      if (match && (bestMatch === null || match.start < bestMatch.start)) {
        bestMatch = match;
        bestRule = rule;
      }
    }

    if (bestMatch === null) break;

    result += str.slice(cursor, bestMatch.start) + bestRule.to;
    cursor = bestMatch.end;
    changed = true;
  }

  if (!changed) {
    return str;
  }

  result += str.slice(cursor);

  if (isLogEnabled()) {
    const label = scene ? `urlTransform/${scene}` : 'urlTransform';
    console.log(
      `[${label}] 域名前缀已替换\n  原始: ${truncate(str)}\n  替换后: ${truncate(result)}`
    );
  }
  return result;
}

export default replaceRelayCosfile;
